<template>
    <Card pageTitle="AGCO Reporting">
        <AllowPermission :permission="PERMISSION_CODES.CMS_VIEW_AGCO_REPORTING">
            <div class="agco-guidance" role="note">
                <strong>How AGCO reporting works</strong>
                <p>
                    GET tabs retrieve data held by AGCO. POST tabs prepare data from this application for review and optional submission.
                    Automatic posting is controlled separately by the toggle shown on each POST tab.
                </p>
            </div>
            <TabContainer @change="handleChangeTab" :tabs="Object.values(AGCO_REPORT_TABS)" :selectedTab="selectedTab" />
            <AgcoReportingTemplate
                title="Get Inventory Event"
                customFetchBtnTitle="Get Data from AGCO"
                dataUrl="inventory-data-api"
                :showPushButton="false"
                :canManage="canManageReporting"
                dataResultKey="inventoryEventList"
                :showDateFilter="true"
                :columnNames="['Event', 'Qty Change', 'Value Change', 'OCS Sku', 'Pos Vendor', 'Posting Timestamp', 'Sold At Price', 'UPC']"
                :columnKeys="[
                    'inventoryEventType',
                    'inventoryQuantityChange',
                    'inventoryValueChange',
                    'ocsSku',
                    'posVendor',
                    'postingDateTimeStamp',
                    'soldAtPrice',
                    'upcAndBarcode',
                ]"
                v-if="showTemplate && selectedTab == AGCO_REPORT_TABS.GET_INVENTORY_EVENT"
            />
            <AgcoReportingTemplate
                title="Get Inventory Position"
                customFetchBtnTitle="Get Data from AGCO"
                dataUrl="position-trigger-data-api"
                :showPushButton="false"
                :canManage="canManageReporting"
                dataResultKey="inventoryPositionList"
                :showDateFilter="true"
                :columnNames="[
                    'Ocs Sku',
                    'Upc And Barcode',
                    'Inventory Quantity On Hand',
                    'Inventory Book Value',
                    'Inventory Position Date',
                ]"
                :columnKeys="['ocsSku', 'upcAndBarcode', 'inventoryQuantityOnHand', 'inventoryBookValue', 'inventoryPositionDate']"
                v-if="showTemplate && selectedTab == AGCO_REPORT_TABS.GET_INVENTORY_POSITION"
            />
            <AgcoReportingTemplate
                title="Post Inventory Event"
                dataUrl="inventory-data"
                :showPushButton="true"
                :canManage="canManageReporting"
                dataResultKey="inventoryEventList"
                :showDateFilter="true"
                cronStatusUpdateUrl="inventory-data-cron-update"
                :columnNames="[
                    'Event',
                    'Qty Change',
                    'Value Change',
                    'OCS Sku',
                    'UPC',
                    'Counter Party CRSA',
                    'Line Transaction ID',
                    'Sold At Price',
                    'Pos Vendor',
                    'Transaction Timestamp',
                ]"
                :columnKeys="[
                    'inventoryEventType',
                    'inventoryQuantityChange',
                    'inventoryValueChange',
                    'ocsSku',
                    'upcAndBarcode',
                    'counterPartyCRSA',
                    'posTransactionLineId',
                    'soldAtPrice',
                    'posVendor',
                    'posTransactionTimeStampFormatted',
                ]"
                v-if="showTemplate && selectedTab == AGCO_REPORT_TABS.POST_INVENTORY_EVENT"
            />
            <AgcoReportingTemplate
                title="Post Inventory Position"
                dataUrl="position-trigger-data"
                :showPushButton="true"
                :canManage="canManageReporting"
                dataResultKey="inventoryPositionList"
                :showDateFilter="true"
                :showStartDate="false"
                cronStatusUpdateUrl="position-trigger-cron-update"
                :columnNames="['Ocs Sku', 'Upc And Barcode', 'Inventory Quantity On Hand', 'Inventory Book Value']"
                :columnKeys="['ocsSku', 'upcAndBarcode', 'inventoryQuantityOnHand', 'inventoryBookValue']"
                v-if="showTemplate && selectedTab == AGCO_REPORT_TABS.POST_INVENTORY_POSITION"
            />
        </AllowPermission>
    </Card>
</template>

<script>
import { mapState } from "vuex"
import Card from "../../../components/UI/Card.vue"
import TabContainer from "../../../components/UI/TabContainer.vue"
import AgcoReportingTemplate from "../../../components/agco-reporting/AgcoReportingTemplate.vue"
import { checkHasPermission, PERMISSION_CODES } from "../../../store/permission"
import AllowPermission from "../../../components/layout/AllowPermission.vue"

const AGCO_REPORT_TABS = {
    GET_INVENTORY_EVENT: {
        name: "GET_INVENTORY_EVENT",
        value: "Get Inventory Event",
    },
    GET_INVENTORY_POSITION: {
        name: "GET_INVENTORY_POSITION",
        value: "Get Inventory Position",
    },
    POST_INVENTORY_EVENT: {
        name: "POST_INVENTORY_EVENT",
        value: "Post Inventory Event",
    },
    POST_INVENTORY_POSITION: {
        name: "POST_INVENTORY_POSITION",
        value: "Post Inventory Position",
    },
}
export default {
    data() {
        return {
            selectedTab: AGCO_REPORT_TABS.GET_INVENTORY_EVENT,
            AGCO_REPORT_TABS,
            showTemplate: true,
            PERMISSION_CODES,
        }
    },
    computed: {
        ...mapState("auth", ["user"]),
        canManageReporting() {
            return checkHasPermission({
                isAdmin: Boolean(this.user?.isAdmin),
                permissionId: PERMISSION_CODES.CMS_EDIT_AGCO_REPORTING,
                userPermissionIds: this.user?.permissionIds || [],
            })
        },
    },
    components: {
        AllowPermission,
        AgcoReportingTemplate,
        TabContainer,
        Card,
    },
    methods: {
        handleChangeTab(e) {
            this.showTemplate = false
            this.selectedTab = e
            setTimeout(() => {
                this.showTemplate = true
            }, 50)
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../../assets/variables.scss";

/* Tokenized spacing between sections */
::v-deep .tab-container {
  margin-bottom: $sectionGap;
}

::v-deep .tab-container.dark .tab-item {
    &.active,
    &:hover {
        border-bottom-color: $secondaryColorDark;
        color: $secondaryColorDark;
    }
}

/* Align numeric columns inside rendered templates where applicable */
::v-deep table {
  th,
  td {
    padding: $tableCellPaddingY $tableCellPaddingX;
  }
}

.agco-guidance {
    margin-bottom: $sectionGap;
    padding: 12px 14px;
    border: 1px solid rgba(127, 127, 127, 0.32);
    border-radius: 8px;
    background: rgba(127, 127, 127, 0.08);
    color: inherit;
    font-size: 14px;
    line-height: 1.45;

    p {
        margin: 4px 0 0;
    }
}

@media (max-width: 575px) {
    ::v-deep .tab-container {
        flex-wrap: wrap;
    }

    ::v-deep .tab-item {
        flex: 1 0 50%;
        min-width: 0;
    }
}
</style>
